/**
 * Público (`/activa/publico`): una fila por persona, juntando la base de ventas, la Base de datos de WhatsApp, las
 * personas de las campañas de Snov y los clientes de los análisis de Reactivación. Pedido de Alan del 12/10/2026: «un
 * lugar central donde vea a mi público con sus segmentaciones y vea si en WhatsApp respondieron o sus emails».
 *
 * DEENEX Activa: para el equipo, la antigüedad de la base de ventas bajo el título y las pestañas Personas | Historial de
 * cargas (`?vista=cargas`) | Con consentimiento (`?vista=consentimientos`: las cargas del oficial, que vivían en la
 * Base de datos hasta el 17/10/2026). La columna y el filtro de segmento quedan hasta que exista Campañas, donde el diseño muestra
 * los grupos (DISENO §2): hoy es el único lugar donde se ve el grupo de cada persona. Decisión de Alan del 13/10/2026.
 *
 * - Los filtros viven en la URL: volver de una ficha deja la tabla como estaba (la ficha recibe a qué URL volver).
 * - La búsqueda corre mientras se escribe; tocar cualquier parte de una fila abre la ficha de la persona.
 * - Los celulares vienen enmascarados del backend; el CSV (del equipo) los trae completos.
 * - El público se arma solo cada 30 minutos; «Actualizar» (del equipo) lo arma ahora y la pantalla espera.
 * - En el celular (debajo de `md`), una tarjeta por persona en vez de la tabla (que pide 860 px), y los botones y la
 *   paginación de 44 px de alto, para el dedo. En la compu, la tabla de siempre.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AtSign, Download, Loader2, MessageCircle, RefreshCw, Search, Users } from "lucide-react";
import { publicoApi } from "../api";
import { ACTIVIDADES, CANALES, type Actividad, type CanalFiltro, type FiltrosPublico, type Orden, type PaginaPublico, type ResumenPublico } from "../types";
import { usePedido } from "../../emails/hooks/usePedido";
import { descargarTexto } from "../../emails/logic/emails";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import { ErrorTarjeta, Esqueleto, Vacio } from "../../wa-oficial/components/estadisticas/comun";
import { money, num } from "../../campana-nueva/utils/format";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { EncabezadoPagina, Pagina } from "../../../components/activa/Pagina";
import { SIN_NOVEDAD, TEXTO_ACTIVIDAD, TEXTO_CANAL, contactoDeLaFila, estadoMail, estadoWhatsApp, haceCuanto, nombreVisible, rutaFicha, textoArmado, type Tono } from "../logic/publico";
import { baseVentasApi } from "../../base-ventas/api";
import type { EstadoBase } from "../../base-ventas/types";
import { LineaBase } from "../../base-ventas/components/LineaBase";
import { HistorialCargas } from "../../base-ventas/components/HistorialCargas";
import CargasPage from "../../wa-oficial/pages/base/CargasPage";

const POR_PAGINA = 50;
/** Cuánto se espera a que se deje de escribir para buscar. */
const ESPERA_BUSQUEDA_MS = 400;

const TONO: Record<Tono, string> = {
  verde: "bg-emerald-50 text-emerald-700",
  violeta: "bg-violet-50 text-violet-700",
  gris: "bg-slate-100 text-slate-500",
  rojo: "bg-red-50 text-red-700",
  ambar: "bg-amber-50 text-amber-800",
};
/** 44 px de alto en el celular (el mínimo para el dedo); en la compu, el de siempre. */
const ALTO_BOTON = "h-11 md:h-9";
const SELECTOR = `${ALTO_BOTON} rounded-lg border border-slate-200 bg-white px-2 text-sm`;

function Chip({ texto, tono, extra }: { texto: string; tono: Tono; extra?: string }) {
  // lo que todavía no pasó va apagado, sin chip: así se ve lo que sí pasó
  if (SIN_NOVEDAD.has(texto)) return <span className="text-xs text-slate-400 whitespace-nowrap">{texto}</span>;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${TONO[tono]}`}>
      {texto}
      {extra && <span className="font-normal opacity-80">· {extra}</span>}
    </span>
  );
}

function Numero({ etiqueta, valor, icono }: { etiqueta: string; valor: number; icono?: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3">
      <p className="text-xs text-slate-500 flex items-center gap-1">{icono} {etiqueta}</p>
      <p className="text-2xl font-semibold text-slate-900 tabular-nums">{num(valor)}</p>
    </div>
  );
}

type Vista = "personas" | "cargas" | "consentimientos";
const VISTAS: { valor: Vista; texto: string }[] = [
  { valor: "personas", texto: "Personas" },
  { valor: "cargas", texto: "Historial de cargas" },
  { valor: "consentimientos", texto: "Con consentimiento" },
];
const esVista = (v: string | null): v is Vista => VISTAS.some((x) => x.valor === v);

/** Las pestañas, con el contrato de `role="tablist"` (flechas, Home/End y roving tabindex), como `CampanaEmailPage`. */
function Pestanas({ vista, onElegir }: { vista: Vista; onElegir: (v: Vista) => void }) {
  const onKeyDown = (e: KeyboardEvent) => {
    const i = VISTAS.findIndex((v) => v.valor === vista);
    let sig = -1;
    if (e.key === "ArrowRight") sig = (i + 1) % VISTAS.length;
    else if (e.key === "ArrowLeft") sig = (i - 1 + VISTAS.length) % VISTAS.length;
    else if (e.key === "Home") sig = 0;
    else if (e.key === "End") sig = VISTAS.length - 1;
    if (sig < 0) return;
    e.preventDefault();
    onElegir(VISTAS[sig].valor);
    document.getElementById(`tab-publico-${VISTAS[sig].valor}`)?.focus();
  };
  return (
    <div role="tablist" aria-label="Qué ver del público" onKeyDown={onKeyDown}
      className="inline-flex max-w-full items-center gap-1 rounded-xl bg-slate-100 p-1 overflow-x-auto">
      {VISTAS.map((v) => {
        const activa = vista === v.valor;
        return (
          <button key={v.valor} id={`tab-publico-${v.valor}`} type="button" role="tab" aria-selected={activa} aria-controls={`panel-publico-${v.valor}`}
            tabIndex={activa ? 0 : -1} onClick={() => onElegir(v.valor)}
            className={`min-h-[44px] px-3 rounded-lg text-sm font-semibold whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${activa ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {v.texto}
          </button>
        );
      })}
    </div>
  );
}

/** Los filtros, leídos de la URL (lo que no se reconoce no filtra). */
function filtrosDe(sp: URLSearchParams): FiltrosPublico {
  const canal = sp.get("canal") || "";
  const actividad = sp.get("actividad") || "";
  const orden = sp.get("orden") || "";
  const pagina = Number(sp.get("pagina") || 1);
  return {
    q: sp.get("q") || undefined,
    segmento: sp.get("segmento") || undefined,
    canal: (CANALES as readonly string[]).includes(canal) ? (canal as CanalFiltro) : undefined,
    actividad: (ACTIVIDADES as readonly string[]).includes(actividad) ? (actividad as Actividad) : undefined,
    orden: orden === "nombre" || orden === "compras" ? (orden as Orden) : undefined,
    pagina: Number.isInteger(pagina) && pagina > 1 ? pagina : undefined,
  };
}

export default function PublicoPage() {
  const equipo = useEsEquipoDeenex();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filtros = useMemo(() => filtrosDe(sp), [sp]);
  // la clave de la tabla son sus filtros: cambiar de pestaña no la vuelve a pedir
  const clave = JSON.stringify(filtros);
  // las pestañas de cargas son del equipo: a una persona de un cliente, `?vista=` le muestra la tabla
  const pedida = sp.get("vista");
  const vista: Vista = equipo && esVista(pedida) ? pedida : "personas";
  const [busqueda, setBusqueda] = useState(filtros.q || "");
  const [aviso, setAviso] = useState("");
  const [bajando, setBajando] = useState(false);
  /** mientras se arma por «Actualizar»: cuándo se había armado antes (para saber cuándo terminó) y desde cuándo se espera */
  const [armando, setArmando] = useState<{ antes: string | null; desde: number } | null>(null);
  const [tic, setTic] = useState(0);

  // `refresco` vuelve a pedir el resumen cada vez que cambia: mientras se arma, cambia cada 3 s
  const resumen = usePedido<ResumenPublico>(() => publicoApi.resumen(), "resumen", { refresco: tic });
  const lista = usePedido<PaginaPublico>(() => publicoApi.lista({ ...filtros, porPagina: POR_PAGINA }), clave);
  // la base de ventas es del equipo: a una persona de un cliente no se le pide
  const base = usePedido<EstadoBase>(() => baseVentasApi.estado(), "base-ventas", { activo: equipo });
  const enCurso = !!resumen.dato?.estado.enCurso;
  const recargarLista = useRef(lista.recargar);
  recargarLista.current = lista.recargar;

  useEffect(() => {
    if (!armando) return;
    const id = setInterval(() => setTic((x) => x + 1), 3000);
    return () => clearInterval(id);
  }, [armando]);

  // terminó de armar (cambió la fecha del último armado, o falló): se deja de mirar y se vuelve a pedir la tabla
  useEffect(() => {
    const e = resumen.dato?.estado;
    if (!armando || !e || e.enCurso) return;
    const termino = e.armadoAt !== armando.antes || !!e.error;
    const seCanso = Date.now() - armando.desde > 120_000;
    if (termino || seCanso) {
      setArmando(null);
      if (seCanso && !termino) setAviso("Sigue armándose: los números se van a actualizar solos en un rato.");
      recargarLista.current();
    }
  }, [armando, resumen.dato]);

  const cambiar = useCallback((cambios: Record<string, string | undefined>) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(cambios)) {
        if (v) n.set(k, v);
        else n.delete(k);
      }
      // cualquier filtro nuevo vuelve a la primera página
      if (!("pagina" in cambios)) n.delete("pagina");
      return n;
    });
  }, [setSp]);

  // cambiar de pestaña deja los filtros de la tabla en la URL: volver a «Personas» la muestra como estaba
  const verPestana = (v: Vista) =>
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (v === "personas") n.delete("vista");
      else n.set("vista", v);
      return n;
    }, { replace: true });

  const buscar = (e: React.FormEvent) => {
    e.preventDefault();
    cambiar({ q: busqueda.trim() || undefined });
  };

  // Busca mientras se escribe (pedido de Alan del 12/10: «revisar que los filtros funcionen»). Sólo con Enter, y
  // sin botón, escribir un nombre no hacía nada y parecía que la búsqueda no andaba.
  useEffect(() => {
    const q = busqueda.trim();
    if (q === (filtros.q || "")) return;
    const id = setTimeout(() => cambiar({ q: q || undefined }), ESPERA_BUSQUEDA_MS);
    return () => clearTimeout(id);
  }, [busqueda, filtros.q, cambiar]);

  // La URL manda (atrás, adelante, «Limpiar filtros»): el cuadro muestra lo que se está buscando.
  useEffect(() => {
    setBusqueda(filtros.q || "");
  }, [filtros.q]);

  /** La ficha sabe a qué tabla volver: la de ahora, con sus filtros y su página. */
  const volverAca = { volver: `${location.pathname}${location.search}` };
  const abrirFicha = (e: React.MouseEvent, clave: string) => {
    // un link o un botón de la fila hacen lo suyo; si se estaba seleccionando texto (para copiar un mail), no se navega
    if ((e.target as HTMLElement).closest("a, button")) return;
    if (window.getSelection()?.toString()) return;
    navigate(rutaFicha(clave), { state: volverAca });
  };

  const actualizar = async () => {
    setAviso("");
    try {
      const antes = resumen.dato?.estado.armadoAt ?? null;
      const l = leerRespuesta(await publicoApi.actualizar());
      if (!l.ok && l.code !== "YA_EN_CURSO") { setAviso(textoError(l)); return; }
      setArmando({ antes, desde: Date.now() });
    } catch (e) {
      setAviso(textoError(leerError(e)));
    }
  };

  const exportar = async () => {
    setBajando(true);
    setAviso("");
    try {
      const { pagina: _p, ...sinPagina } = filtros;
      const l = leerRespuesta<{ nombre: string; contenido: string; filas: number; recortado: boolean }>(await publicoApi.exportar(sinPagina));
      if (!l.ok || !l.data) { setAviso(l.ok ? "No pudimos armar el archivo." : textoError(l)); return; }
      descargarTexto(l.data.nombre, l.data.contenido);
      if (l.data.recortado) setAviso(`El archivo trae las primeras ${num(l.data.filas)} personas: filtrá para bajar el resto.`);
    } catch (e) {
      setAviso(textoError(leerError(e)));
    } finally {
      setBajando(false);
    }
  };

  const r = resumen.dato;
  const t = r?.totales;
  const nuncaArmado = !!r && !r.estado.armadoAt && !r.estado.enCurso && r.totales.personas === 0;
  const pag = lista.dato;
  const desde = pag && pag.total ? (pag.pagina - 1) * pag.porPagina + 1 : 0;
  const hasta = pag ? Math.min(pag.total, pag.pagina * pag.porPagina) : 0;
  const hayFiltros = !!(filtros.q || filtros.segmento || filtros.canal || filtros.actividad);

  return (
    <Pagina>
      <EncabezadoPagina
        antetitulo="Público"
        titulo="Tu público"
        bajada="Una fila por persona: junta tus ventas, la base de WhatsApp y los mails de Snov."
        acciones={equipo && (
          <>
            <button type="button" onClick={() => void actualizar()} disabled={enCurso || !!armando}
              className={`${ALTO_BOTON} px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50`}>
              {enCurso || !!armando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar
            </button>
            <button type="button" onClick={() => void exportar()} disabled={bajando || !t?.personas}
              className={`${ALTO_BOTON} px-3 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5 disabled:opacity-50`}>
              {bajando ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Exportar CSV
            </button>
          </>
        )}
      >
        {equipo && base.dato && <LineaBase estado={base.dato} />}
        {r && (
          <p className={`text-xs ${r.estado.error ? "text-amber-700" : "text-slate-400"}`} data-testid="estado-armado">
            {textoArmado(r.estado.armadoAt, enCurso || !!armando)}
            {r.estado.error && !enCurso && " La última vez no se pudo actualizar: se muestra lo anterior."}
          </p>
        )}
      </EncabezadoPagina>

      {aviso && <p role="status" className="text-sm text-amber-800 bg-amber-50 rounded-xl px-4 py-2">{aviso}</p>}

      {resumen.error && !r ? (
        <ErrorTarjeta texto={resumen.error} onReintentar={resumen.recargar} />
      ) : !r ? (
        <Esqueleto alto="h-24" etiqueta="Cargando los números" />
      ) : nuncaArmado ? (
        <Vacio>
          <p className="font-semibold text-slate-700">Todavía no se armó el público de este comercio.</p>
          <p className="mt-1">Se arma solo en unos minutos.{equipo ? " Si no querés esperar, tocá «Actualizar»." : ""}</p>
        </Vacio>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="numeros">
          <Numero etiqueta="Personas" valor={t!.personas} icono={<Users size={12} />} />
          <Numero etiqueta="Con WhatsApp" valor={t!.conWhatsApp} icono={<MessageCircle size={12} />} />
          <Numero etiqueta="Con mail" valor={t!.conMail} icono={<AtSign size={12} />} />
          <Numero etiqueta="Respondieron" valor={t!.respondieron} />
          <Numero etiqueta="Pidieron la baja" valor={t!.bajas} />
        </div>
      )}

      <section className="rounded-2xl border border-slate-100 bg-white p-4 space-y-3">
        {equipo && <Pestanas vista={vista} onElegir={verPestana} />}

        {vista === "cargas" ? (
          <div role="tabpanel" id="panel-publico-cargas" aria-labelledby="tab-publico-cargas">
            <HistorialCargas />
          </div>
        ) : vista === "consentimientos" ? (
          <div role="tabpanel" id="panel-publico-consentimientos" aria-labelledby="tab-publico-consentimientos">
            <CargasPage />
          </div>
        ) : (
          <div className="space-y-3" {...(equipo ? { role: "tabpanel", id: "panel-publico-personas", "aria-labelledby": "tab-publico-personas" } : {})}>
            <div className="flex flex-wrap gap-2 items-center">
              <form onSubmit={buscar} className="flex-1 min-w-[220px] relative" role="search">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por nombre, mail o celular"
                  aria-label="Buscar por nombre, mail o celular"
                  className={`w-full ${ALTO_BOTON} rounded-lg border border-slate-200 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500`} />
              </form>
              <select aria-label="Segmento" value={filtros.segmento || ""} onChange={(e) => cambiar({ segmento: e.target.value || undefined })}
                className={SELECTOR}>
                <option value="">Todos los segmentos</option>
                {(r?.segmentos || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.emoji ? `${s.emoji} ` : ""}{s.nombre} ({num(s.personas)})</option>
                ))}
              </select>
              <select aria-label="Canal" value={filtros.canal || ""} onChange={(e) => cambiar({ canal: e.target.value || undefined })}
                className={SELECTOR}>
                <option value="">Cualquier canal</option>
                {CANALES.map((c) => <option key={c} value={c}>{TEXTO_CANAL[c]}</option>)}
              </select>
              <select aria-label="Qué hizo" value={filtros.actividad || ""} onChange={(e) => cambiar({ actividad: e.target.value || undefined })}
                className={SELECTOR}>
                <option value="">Cualquier actividad</option>
                {ACTIVIDADES.map((a) => <option key={a} value={a}>{TEXTO_ACTIVIDAD[a]}</option>)}
              </select>
              <select aria-label="Orden" value={filtros.orden || "reciente"} onChange={(e) => cambiar({ orden: e.target.value === "reciente" ? undefined : e.target.value })}
                className={SELECTOR}>
                <option value="reciente">Lo último primero</option>
                <option value="nombre">Por nombre</option>
                <option value="compras">Los que más compraron</option>
              </select>
              {hayFiltros && (
                <button type="button" onClick={() => setSp(new URLSearchParams())} className="min-h-[44px] md:min-h-0 text-xs font-semibold text-violet-600 hover:text-violet-800">
                  Limpiar filtros
                </button>
              )}
            </div>

            {lista.error && !pag ? (
              <ErrorTarjeta texto={lista.error} onReintentar={lista.recargar} />
            ) : !pag ? (
              <Esqueleto alto="h-64" etiqueta="Cargando el público" />
            ) : pag.total === 0 ? (
              <Vacio>{hayFiltros ? "Nadie cumple esos filtros." : "Todavía no hay personas en el público."}</Vacio>
            ) : (
              <>
                {/* En el celular, una tarjeta por persona con lo principal; tocarla abre la ficha, como la fila. */}
                <ul className={`md:hidden space-y-2 ${lista.cargando ? "opacity-60" : ""}`} data-testid="tarjetas-publico">
                  {pag.personas.map((p) => {
                    const wa = estadoWhatsApp(p);
                    const ml = estadoMail(p);
                    return (
                      <li key={p.clave} onClick={(e) => abrirFicha(e, p.clave)} className="rounded-xl border border-slate-200 bg-white p-3 cursor-pointer active:bg-violet-50/40">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <Link to={rutaFicha(p.clave)} state={volverAca} className="font-semibold text-slate-900 hover:text-violet-700">{nombreVisible(p)}</Link>
                            <p className="text-xs text-slate-400 truncate">{contactoDeLaFila(p)}</p>
                          </div>
                          {p.segmento && <span className="text-xs text-slate-700 whitespace-nowrap">{p.segmento.emoji} {p.segmento.nombre}</span>}
                        </div>
                        <dl className="mt-2 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-xs">
                          <dt className="text-slate-500">WhatsApp</dt>
                          <dd><Chip texto={wa.texto} tono={wa.tono} extra={wa.via} /></dd>
                          <dt className="text-slate-500">Mail</dt>
                          <dd><Chip texto={ml.texto} tono={ml.tono} /></dd>
                          <dt className="text-slate-500">Última compra</dt>
                          <dd className="text-slate-600">
                            {p.compras?.ultimaCompra ? haceCuanto(p.compras.ultimaCompra) : p.compras ? "Nunca compró" : "—"}
                            {!!p.compras?.montoTotal && <> · gastó {money(p.compras.montoTotal)} en total</>}
                          </dd>
                        </dl>
                      </li>
                    );
                  })}
                </ul>
                <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                  <table className="w-full text-sm min-w-[860px]" data-testid="tabla-publico">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                        <th className="py-2 px-3 font-medium">Persona</th>
                        <th className="py-2 px-3 font-medium">Segmento</th>
                        <th className="py-2 px-3 font-medium">WhatsApp</th>
                        <th className="py-2 px-3 font-medium">Mail</th>
                        <th className="py-2 px-3 font-medium">Última compra</th>
                        <th className="py-2 px-3 font-medium text-right">Gastó en total</th>
                        <th className="py-2 px-3 font-medium">Última actividad</th>
                      </tr>
                    </thead>
                    <tbody className={lista.cargando ? "opacity-60" : ""}>
                      {pag.personas.map((p) => {
                        const wa = estadoWhatsApp(p);
                        const ml = estadoMail(p);
                        return (
                          // Toda la fila abre la ficha (pedido de Alan del 12/10: «cuando toco arriba de la información, que me
                          // abra la ficha»). El nombre sigue siendo el link: con el teclado, o para abrirla en otra pestaña.
                          <tr key={p.clave} onClick={(e) => abrirFicha(e, p.clave)} className="border-b border-slate-50 hover:bg-violet-50/40 cursor-pointer">
                            <td className="py-2.5 px-3">
                              <Link to={rutaFicha(p.clave)} state={volverAca} className="font-semibold text-slate-900 hover:text-violet-700">{nombreVisible(p)}</Link>
                              <p className="text-xs text-slate-400 truncate max-w-[220px]">{contactoDeLaFila(p)}</p>
                            </td>
                            <td className="py-2.5 px-3">
                              {p.segmento ? (
                                <span className="text-xs text-slate-700 whitespace-nowrap">{p.segmento.emoji} {p.segmento.nombre}</span>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>
                            <td className="py-2.5 px-3"><Chip texto={wa.texto} tono={wa.tono} extra={wa.via} /></td>
                            <td className="py-2.5 px-3"><Chip texto={ml.texto} tono={ml.tono} /></td>
                            <td className="py-2.5 px-3 text-xs text-slate-600 whitespace-nowrap">
                              {p.compras?.ultimaCompra ? haceCuanto(p.compras.ultimaCompra) : p.compras ? "Nunca compró" : "—"}
                            </td>
                            <td className="py-2.5 px-3 text-xs text-slate-700 whitespace-nowrap text-right tabular-nums">
                              {p.compras?.montoTotal ? money(p.compras.montoTotal) : "—"}
                            </td>
                            <td className="py-2.5 px-3 text-xs text-slate-500 whitespace-nowrap">{haceCuanto(p.ultimaInteraccionAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{num(desde)} a {num(hasta)} de {num(pag.total)}</span>
                  <div className="flex gap-2">
                    <button type="button" disabled={pag.pagina <= 1} onClick={() => cambiar({ pagina: pag.pagina > 2 ? String(pag.pagina - 1) : undefined })}
                      className="h-11 md:h-8 px-3 rounded-lg border border-slate-200 disabled:opacity-40">Anterior</button>
                    <button type="button" disabled={hasta >= pag.total} onClick={() => cambiar({ pagina: String(pag.pagina + 1) })}
                      className="h-11 md:h-8 px-3 rounded-lg border border-slate-200 disabled:opacity-40">Siguiente</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </Pagina>
  );
}
